import React, { useEffect, useState } from "react";
import { ScrollText, RefreshCw } from "lucide-react";
import PeopleLayout from "../../components/PeopleLayout";
import { peopleApi, formatDetail } from "../../lib/api";

const ACTION_TONE = {
  REVIEW_APPROVED: "text-emerald-300",
  REVIEW_REJECTED: "text-red-400",
  MANUAL_OVERRIDE_CREATED: "text-amber-300",
};

export default function PeopleAudit() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const refresh = async () => {
    try {
      const r = await peopleApi.get("/people/audit?limit=300");
      setRows(r.data);
      setErr("");
    } catch (e) {
      setErr(formatDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  return (
    <PeopleLayout
      title="Audit Ledger"
      subtitle="Append-only record of every workforce decision · source=PEOPLE_ENGINE"
      actions={
        <button
          data-testid="pe-audit-refresh"
          onClick={refresh}
          className="h-10 px-4 border border-neutral-700 hover:border-amber-400 hover:text-amber-300 font-mono text-xs uppercase tracking-wider inline-flex items-center gap-2"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      }
    >
      {loading && <div className="font-mono text-xs text-neutral-500">Loading…</div>}

      {!loading && rows.length === 0 && (
        <div
          data-testid="pe-audit-empty"
          className="border border-neutral-800 bg-[#13161C] p-12 text-center"
        >
          <ScrollText size={28} className="text-neutral-700 mx-auto" />
          <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.32em] text-neutral-500">
            No entries yet
          </div>
          <p className="mt-2 text-neutral-400 text-sm">
            Review approvals, rejections, and manual overrides will appear here as they happen.
          </p>
        </div>
      )}

      <div className="space-y-2">
        {rows.map((r) => (
          <div
            key={r.id}
            className="border border-neutral-800 bg-[#13161C] p-4 grid grid-cols-12 gap-3"
            data-testid={`pe-audit-row-${r.id}`}
          >
            <div className="col-span-12 md:col-span-3">
              <div className={`font-mono text-[11px] uppercase tracking-[0.18em] ${ACTION_TONE[r.action] || "text-neutral-300"}`}>
                {r.action}
              </div>
              <div className="font-mono text-[10px] text-neutral-500 mt-1">
                {new Date(r.created_at_iso).toLocaleString("en-GB")}
              </div>
            </div>
            <div className="col-span-12 md:col-span-3 font-mono text-[11px] text-neutral-300">
              <div><span className="text-neutral-600">actor</span> {r.actor_email || r.actor_user_id}</div>
              <div><span className="text-neutral-600">role</span> {r.actor_role}</div>
              <div><span className="text-neutral-600">capability</span> <span className="text-amber-300/80">{r.capability_used || "—"}</span></div>
            </div>
            <div className="col-span-12 md:col-span-3 font-mono text-[11px] text-neutral-300">
              <div><span className="text-neutral-600">attendance</span> {r.attendance_record_id || "—"}</div>
              {r.review_id && (
                <div><span className="text-neutral-600">review</span> {r.review_id}</div>
              )}
            </div>
            <div className="col-span-12 md:col-span-3 font-mono text-[11px] text-neutral-300">
              <div>
                <span className="text-neutral-600">payroll</span>{" "}
                <span className="text-neutral-400">{r.previous_payroll_status || "—"}</span>
                <span className="text-neutral-600"> → </span>
                <span className="text-amber-300">{r.new_payroll_status || "—"}</span>
              </div>
              {r.reason && (
                <div className="mt-1"><span className="text-neutral-600">reason</span> {r.reason}</div>
              )}
              {r.notes && (
                <div className="mt-1 text-neutral-500 italic">“{r.notes}”</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {err && (
        <div className="mt-4 text-red-400 font-mono text-sm" data-testid="pe-audit-error">
          {err}
        </div>
      )}
    </PeopleLayout>
  );
}
